'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {
    InputText,
    InputTextProps
} from 'primereact/inputtext';

import {__} from '~/shared/src/utilities/messageCatalog';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {StoreState} from '~/frontend/src/store';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';


type InputProps = Omit<InputTextProps, 'autoComplete' | 'onChange' | 'key'>;
const inputProps = keys<InputProps>();

type InputChangeProps = React.ChangeEvent<HTMLInputElement>;

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * Show a button for linking.
     */
    linkButton? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    linkButton     : true,
    markDirty      : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The search value.
     */
    value? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    onBlur? : ( event : FocusEvent ) => void;
    onFocus? : ( event : FocusEvent ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const LinkField = React.forwardRef<InputText, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  id,
                  value,
                  originalValue,
                  autoComplete,
                  disabled,
                  linkButton,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  placeholder,
                  inputClassNames,
                  inputStyle,
                  onChange,
                  onIntermediateChange,
                  onFocus,
                  onBlur,
                  pt,
                  tabIndex,
                  useHookForm
              }                                 = props,
              elementId                         = id ?? `${uid}_linkField`,
              inValue                           = useRef<string | undefined>( value ),
              outValue                          = useRef<string | undefined>( value ),
              timeoutId                         = useRef<NodeJS.Timeout | undefined>(),
              [ elementValue, setElementValue ] = useState<string | undefined>( value ),
              [ focus, setFocus ]               = useState<boolean>( false ),
              messageResolver                   = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder               = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              fieldWrapperAutoCompleteConfig    = useMemo(
                  () => {
                      let retVal : Partial<FieldWrapperComponentProps> = {};

                      if ( autoComplete && !disabled ) {
                          retVal = {
                              autoCompleteValue        : elementValue,
                              autoCompleteInputHasFocus: focus,
                              onAutoCompleteValueChange: ( autoCompleteValueRaw : string, originalEvent : any ) => {
                                  if ( autoCompleteValueRaw !== elementValue ) {
                                      setElementValue( autoCompleteValueRaw );

                                      onChange( autoCompleteValueRaw, originalEvent );
                                  }
                              }
                          };
                      }

                      return retVal;
                  },
                  [
                      autoComplete,
                      disabled,
                      elementValue,
                      focus,
                      onChange
                  ]
              ),
              onInputChange                     = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange             = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onFieldFocus                      = useCallback(
                  () => {
                      const wrapper = document.getElementById( `${elementId}_wrapper` );

                      if ( wrapper ) {
                          wrapper.classList.add( 'p-focus' );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onFieldBlur                       = useCallback(
                  () => {
                      const wrapper = document.getElementById( `${elementId}_wrapper` );

                      if ( wrapper ) {
                          wrapper.classList.remove( 'p-focus' );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onInputFocus                      = useCallback(
                  ( event : FocusEvent ) => {
                      setFocus( true );

                      onFieldFocus();
                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                       = useCallback(
                  ( event : FocusEvent ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onFieldBlur();
                      onBlur?.( event );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              onLinkButtonClick                 = useCallback(
                  () => {
                      const link = document.createElement( 'a' ),
                            url  = outValue.current?.match( /:\/\// ) ? outValue.current : `https://${outValue.current}`;

                      link.setAttribute( 'href', url );
                      link.setAttribute( 'target', '_blank' );
                      link.click();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              getInput                          = () => {
                  let retVal = <InputText
                      {..._.pick( props, inputProps ) as InputProps}

                      id={elementId}

                      ref={forwardedRef}

                      value={elementValue}

                      pt={{
                          root: {
                              onChange: useHookForm ? onHookFormInputChange : onInputChange,
                              onFocus : onInputFocus,
                              onBlur  : onInputBlur
                          }
                      }}

                      className={`yn-linkfield ${inputClassNames.join( ' ' )}`}
                      style={{ ...defaultProps.inputStyle, ...inputStyle }}

                      placeholder={resolvedPlaceholder}
                      tooltip={undefined}
                      tooltipOptions={undefined}

                      aria-describedby={`${elementId}_message`}

                      autoComplete={autoComplete && !disabled ? 'off' : 'on'}
                      aria-autocomplete={autoComplete && !disabled ? 'list' : undefined}
                      aria-controls={autoComplete && !disabled ? `${elementId}_autocomplete_list` : undefined}
                      aria-haspopup={autoComplete && !disabled ? 'listbox' : undefined}
                  />;

                  if ( linkButton ) {
                      retVal = <div id={`${elementId}_wrapper`} className={`p-inputgroup yn-inputgroup yn-subfield${disabled ? ' p-disabled yn-enabled-button' : ''}`}>
                          {retVal}
                          <Button
                              icon="pi pi-link"
                              className={( elementValue ?? '' ) !== '' ? 'yn-enabled' : ''}
                              disabled={( elementValue ?? '' ) === ''}

                              onClick={onLinkButtonClick}
                              onFocus={onFieldFocus}
                              onBlur={onFieldBlur}

                              tabIndex={tabIndex ? tabIndex + 10 : 0}
                          />
                      </div>;
                  }

                  return retVal;
              };

        inValue.current = value;
        outValue.current = elementValue;

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && ( elementValue ?? '' ) !== ( originalValue ?? '' )}
            displayValueWidthOffsetRem={linkButton ? 2.357 : undefined}
            displayValueWidthOffsetPx={linkButton ? 1 : undefined}

            {...fieldWrapperAutoCompleteConfig}
            pt={pt?.fieldWrapper}
        >
            {getInput()}
        </FieldWrapper>;
    }
} );
LinkField.defaultProps = defaultProps;
LinkField.displayName = 'LinkField';

export default LinkField;
